/**
 * Subscription Detection Diagnostics
 * 
 * Tools to diagnose and understand subscription detection issues
 * without the noise of complex logging systems.
 */

import { supabase } from '@/integrations/supabase/client';
import { isIOSNativeApp } from '@/utils/platformUtils';

export interface DiagnosticResult {
  timestamp: string;
  category: string;
  status: 'success' | 'warning' | 'error' | 'info';
  message: string;
  data?: Record<string, unknown>;
}

export class SubscriptionDiagnostics {
  private results: DiagnosticResult[] = [];

  private log(category: string, status: DiagnosticResult['status'], message: string, data?: Record<string, unknown>) {
    const result: DiagnosticResult = {
      timestamp: new Date().toISOString(),
      category,
      status,
      message,
      data
    };
    this.results.push(result);
    
    // Also log to console with clear prefix
    const prefix = `🔍 DIAG [${category}]`;
    const logData = data ? ` | Data: ${JSON.stringify(data, null, 2)}` : '';
    console.log(`${prefix} ${status.toUpperCase()}: ${message}${logData}`);
  }

  /**
   * Run complete subscription detection diagnostics
   */
  async runFullDiagnostics(userId?: string): Promise<DiagnosticResult[]> {
    this.results = [];
    this.log('DIAGNOSTICS', 'info', 'Starting full subscription diagnostics');

    // 1. Platform Detection
    await this.checkPlatform();

    // 2. User Authentication
    if (userId) {
      await this.checkUserAuth(userId);
      await this.checkDatabaseState(userId);
    } else {
      this.log('AUTH', 'warning', 'No user ID provided, skipping user-specific checks');
    }

    // 3. iOS IAP State (if applicable)
    if (isIOSNativeApp()) {
      await this.checkIOSIAPState();
      await this.analyzeIAPFlow();
    }

    // 4. Permissions State
    if (userId) {
      await this.checkPermissionsState(userId);
    }

    this.log('DIAGNOSTICS', 'info', `Diagnostics complete. Found ${this.results.length} results`);
    return this.results;
  }

  /**
   * Check platform detection
   */
  private async checkPlatform() {
    const isIOS = isIOSNativeApp();
    const platform = navigator.platform;
    const userAgent = navigator.userAgent;

    this.log('PLATFORM', 'info', `Platform detection`, {
      isIOSNativeApp: isIOS,
      platform,
      userAgent: userAgent.substring(0, 100) + '...'
    });

    if (isIOS) {
      this.log('PLATFORM', 'success', 'iOS native app detected - IAP should be available');
    } else {
      this.log('PLATFORM', 'info', 'Not iOS native app - IAP not applicable');
    }
  }

  /**
   * Check user authentication state
   */
  private async checkUserAuth(userId: string) {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        this.log('AUTH', 'error', 'Failed to get session', { error: error.message });
        return;
      }

      if (!session) {
        this.log('AUTH', 'warning', 'No active session found');
        return;
      }

      if (session.user.id !== userId) {
        this.log('AUTH', 'warning', 'Session user ID mismatch', {
          sessionUserId: session.user.id,
          providedUserId: userId
        });
        return;
      }

      this.log('AUTH', 'success', 'User authentication verified', {
        userId: session.user.id,
        email: session.user.email
      });
    } catch (error) {
      this.log('AUTH', 'error', 'Exception during auth check', { error });
    }
  }

  /**
   * Check database state for subscription mappings
   */
  private async checkDatabaseState(userId: string) {
    try {
      // Check subscription_platform_mapping
      const { data: subscriptions, error: subError } = await supabase
        .from('subscription_platform_mapping')
        .select('*')
        .eq('user_id', userId);

      if (subError) {
        this.log('DATABASE', 'error', 'Failed to query subscription mappings', { error: subError.message });
      } else {
        this.log('DATABASE', 'info', `Found ${subscriptions?.length || 0} subscription mappings`, {
          subscriptions: subscriptions?.map((sub: Record<string, unknown>) => ({
            id: sub.id,
            primary_platform: sub.primary_platform,
            status: sub.status,
            ios_original_transaction_id: sub.ios_original_transaction_id,
            created_at: sub.created_at
          }))
        });
      }

      // Check cross_platform_transactions
      const { data: transactions, error: txError } = await supabase
        .from('cross_platform_transactions')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(5);

      if (txError) {
        this.log('DATABASE', 'error', 'Failed to query transactions', { error: txError.message });
      } else {
        this.log('DATABASE', 'info', `Found ${transactions?.length || 0} recent transactions`, {
          transactions: transactions?.map((tx: Record<string, unknown>) => ({
            id: tx.id,
            platform: tx.platform,
            status: tx.status,
            platform_transaction_id: tx.platform_transaction_id,
            created_at: tx.created_at
          }))
        });
      }

      // Check profiles table
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('premium_override, unified_subscription_id')
        .eq('id', userId)
        .single();

      if (profileError) {
        this.log('DATABASE', 'error', 'Failed to query profile', { error: profileError.message });
      } else {
        this.log('DATABASE', 'info', 'Profile data', {
          premium_override: profile?.premium_override,
          unified_subscription_id: profile?.unified_subscription_id
        });
      }

    } catch (error) {
      this.log('DATABASE', 'error', 'Exception during database check', { error });
    }
  }

  /**
   * Check iOS IAP state
   */
  private async checkIOSIAPState() {
    try {
      // Check if cordova plugin is available
      const hasCordova = typeof window !== 'undefined' && 'CdvPurchase' in window;
      this.log('IOS_IAP', 'info', `Cordova plugin availability`, { hasCordova });

      if (!hasCordova) {
        this.log('IOS_IAP', 'warning', 'CdvPurchase not available - plugin may not be loaded');
        return;
      }

      // Check store state
      const windowWithCordova = window as unknown as { CdvPurchase?: { store?: Record<string, unknown> } };
      const store = windowWithCordova.CdvPurchase?.store;
      if (store) {
        this.log('IOS_IAP', 'success', 'Store object available');

        // Check products
        const products = (store.products as Record<string, unknown>[]) || [];
        this.log('IOS_IAP', 'info', `Store has ${products.length} products`, {
          productIds: products.map((p: Record<string, unknown>) => p.id),
          productStates: products.map((p: Record<string, unknown>) => ({
            id: p.id,
            state: p.state,
            owned: p.owned,
            hasTransaction: !!(p as { transaction?: unknown }).transaction,
            hasReceipt: !!(p as { transaction?: { receipt?: string } }).transaction?.receipt
          }))
        });

        // CRITICAL: Detailed transaction and receipt analysis for owned products
        const ownedProducts = products.filter((p: Record<string, unknown>) => p.owned);
        if (ownedProducts.length > 0) {
          for (const product of ownedProducts) {
            const p = product as {
              id?: string;
              transaction?: {
                id?: string;
                receipt?: string;
                [key: string]: unknown;
              };
              receipt?: string;
              [key: string]: unknown;
            };

            this.log('IOS_IAP', 'info', `Owned product analysis: ${p.id}`, {
              productId: p.id,
              owned: p.owned,
              hasTransaction: !!p.transaction,
              hasReceipt: !!(p.transaction?.receipt || p.receipt),
              transactionComplete: !!(p.transaction?.id && (p.transaction?.receipt || p.receipt)),
              transactionDetails: {
                transactionId: p.transaction?.id,
                receiptLength: (p.transaction?.receipt || p.receipt)?.length || 0,
                transactionKeys: p.transaction ? Object.keys(p.transaction) : [],
                productReceiptAvailable: !!p.receipt
              }
            });
          }
        }

        // Check for callback registration
        await this.checkCallbackRegistration(products);

        // CRITICAL: Database integration diagnostic for owned products
        await this.runDatabaseIntegrationDiagnostic(ownedProducts);

        // CRITICAL: Check receipt validation service availability
        await this.runReceiptValidationServiceDiagnostic();

        // Run simple verification diagnostic
        await this.runSimpleVerificationDiagnostic();
      } else {
        this.log('IOS_IAP', 'warning', 'Store object not available');
      }

    } catch (error) {
      this.log('IOS_IAP', 'error', 'Exception during iOS IAP check', { error });
    }
  }

  /**
   * Run receipt validation service diagnostic
   */
  private async runReceiptValidationServiceDiagnostic() {
    try {
      // Check if iOS IAP service has current user ID
      const iosIAPService = await import('../../services/iap/IOSInAppPurchaseService');
      const serviceInstance = iosIAPService.iosIAPService;

      // Get current user ID from service (if available)
      const hasUserId = !!(serviceInstance as unknown as { currentUserId?: string }).currentUserId;
      const userId = (serviceInstance as unknown as { currentUserId?: string }).currentUserId;

      this.log('IOS_IAP', 'info', 'Receipt validation service status', {
        hasIOSIAPService: !!serviceInstance,
        hasCurrentUserId: hasUserId,
        currentUserId: userId || 'MISSING',
        serviceInitialized: !!(serviceInstance as unknown as { isInitialized?: boolean }).isInitialized
      });

    } catch (error) {
      this.log('IOS_IAP', 'error', 'Receipt validation service diagnostic failed', { error });
    }
  }

  /**
   * Run database integration diagnostic for owned products
   */
  private async runDatabaseIntegrationDiagnostic(ownedProducts: Record<string, unknown>[]) {
    try {
      this.log('IOS_IAP', 'info', 'Analyzing database integration for owned products');

      for (const product of ownedProducts) {
        const p = product as {
          id?: string;
          transaction?: {
            id?: string;
            receipt?: string;
            [key: string]: unknown;
          };
          receipt?: string;
          [key: string]: unknown;
        };

        // Check if this product should have created database records
        const hasRequiredData = !!(p.transaction?.id && (p.transaction?.receipt || p.receipt));

        this.log('IOS_IAP', 'info', `Database integration analysis: ${p.id}`, {
          productId: p.id,
          hasRequiredDataForDB: hasRequiredData,
          missingForDB: {
            noTransactionId: !p.transaction?.id,
            noReceipt: !(p.transaction?.receipt || p.receipt),
            productIdIssue: p.id === 'appstore.application'
          },
          availableData: {
            transactionId: p.transaction?.id || 'MISSING',
            receiptAvailable: !!(p.transaction?.receipt || p.receipt),
            receiptSource: p.transaction?.receipt ? 'transaction' : p.receipt ? 'product' : 'none',
            receiptLength: (p.transaction?.receipt || p.receipt)?.length || 0
          },
          databaseIntegrationStatus: hasRequiredData ? 'SHOULD_WORK' : 'MISSING_DATA'
        });
      }
    } catch (error) {
      this.log('IOS_IAP', 'error', 'Database integration diagnostic failed', { error });
    }
  }

  /**
   * Run simple verification diagnostic
   */
  private async runSimpleVerificationDiagnostic() {
    try {
      const { iosIAPService } = await import('@/services/iap/IOSInAppPurchaseService');
      iosIAPService.runIAPDiagnostic();
    } catch (error) {
      this.log('IOS_IAP', 'error', 'Failed to run simple verification diagnostic', { error });
    }
  }

  /**
   * Check if IAP callbacks are properly registered
   */
  private async checkCallbackRegistration(products: Record<string, unknown>[]) {
    try {
      this.log('IOS_IAP', 'info', 'Checking callback registration for products');

      for (const product of products) {
        const productId = product.id as string;
        if (!productId) continue;

        const productWithCallbacks = product as {
          approved?: unknown;
          verified?: unknown;
          updated?: unknown;
          error?: unknown;
        };

        this.log('IOS_IAP', 'info', `Product ${productId} callback availability`, {
          productId,
          hasApproved: typeof productWithCallbacks.approved === 'function',
          hasVerified: typeof productWithCallbacks.verified === 'function',
          hasUpdated: typeof productWithCallbacks.updated === 'function',
          hasError: typeof productWithCallbacks.error === 'function'
        });
      }
    } catch (error) {
      this.log('IOS_IAP', 'error', 'Failed to check callback registration', { error });
    }
  }

  /**
   * Start monitoring IAP events in real-time
   */
  public startIAPEventMonitoring(): void {
    try {
      this.log('IOS_IAP', 'info', 'Starting real-time IAP event monitoring');

      // Monitor console logs for IAP events
      const originalConsoleLog = console.log;
      console.log = (...args: unknown[]) => {
        // Call original console.log
        originalConsoleLog.apply(console, args);

        // Check if this is an IAP-related log
        const message = args.join(' ');
        if (message.includes('iOS IAP Service:')) {
          this.captureIAPEvent(message, args);
        }
      };

      this.log('IOS_IAP', 'success', 'IAP event monitoring started - will capture all iOS IAP Service events');
    } catch (error) {
      this.log('IOS_IAP', 'error', 'Failed to start IAP event monitoring', { error });
    }
  }

  /**
   * Capture and categorize IAP events
   */
  private captureIAPEvent(message: string, args: unknown[]): void {
    try {
      // Categorize the event
      let eventType = 'unknown';
      let severity: 'info' | 'success' | 'warning' | 'error' = 'info';

      if (message.includes('Product approved')) {
        eventType = 'purchase_approved';
        severity = 'success';
      } else if (message.includes('Verification triggered')) {
        eventType = 'verification_triggered';
        severity = 'info';
      } else if (message.includes('Product verified')) {
        eventType = 'purchase_verified';
        severity = 'success';
      } else if (message.includes('Purchase completed successfully')) {
        eventType = 'purchase_completed';
        severity = 'success';
      } else if (message.includes('Subscription mapping created')) {
        eventType = 'subscription_mapping_created';
        severity = 'success';
      } else if (message.includes('Product object structure')) {
        eventType = 'transaction_data_debug';
        severity = 'info';
      } else if (message.includes('Generated transaction ID from receipt')) {
        eventType = 'receipt_based_transaction_id';
        severity = 'info';
      } else if (message.includes('Failed to') || message.includes('❌')) {
        eventType = 'error';
        severity = 'error';
      } else if (message.includes('⚠️')) {
        eventType = 'warning';
        severity = 'warning';
      }

      this.log('IOS_IAP', severity, `Real-time event: ${eventType}`, {
        eventType,
        originalMessage: message,
        timestamp: new Date().toISOString(),
        args: args.length > 1 ? args.slice(1) : undefined
      });
    } catch (error) {
      // Don't log errors in the event capture to avoid infinite loops
    }
  }

  /**
   * Stop monitoring IAP events
   */
  public stopIAPEventMonitoring(): void {
    // Note: In a real implementation, we'd restore the original console.log
    // For now, we'll just log that monitoring is conceptually stopped
    this.log('IOS_IAP', 'info', 'IAP event monitoring stopped');
  }

  /**
   * Check permissions state
   */
  private async checkPermissionsState(userId: string) {
    try {
      // Call the permissions edge function directly
      const { data, error } = await supabase.functions.invoke('check-permissions', {
        body: JSON.stringify({ userId })
      });

      if (error) {
        this.log('PERMISSIONS', 'error', 'Failed to check permissions', { error });
      } else {
        this.log('PERMISSIONS', 'success', 'Permissions check result', {
          isPremium: data?.isPremium,
          isAdmin: data?.isAdmin,
          source: data?.source,
          timestamp: data?.timestamp
        });
      }

    } catch (error) {
      this.log('PERMISSIONS', 'error', 'Exception during permissions check', { error });
    }
  }

  /**
   * Analyze IAP callback flow and transaction data
   */
  private async analyzeIAPFlow(): Promise<void> {
    try {
      this.log('IOS_IAP', 'info', 'Analyzing IAP callback flow and transaction data');

      // Check if we have any recent transactions
      const recentTransactions = this.results
        .filter(r => r.category === 'DATABASE' && r.data?.transactions)
        .flatMap(r => r.data?.transactions || []);

      if (recentTransactions.length > 0) {
        this.log('IOS_IAP', 'info', 'Recent transaction analysis', {
          transactionCount: recentTransactions.length,
          transactions: recentTransactions.map((t: unknown) => {
            const transaction = t as { id: string; status: string; platform_transaction_id: string; created_at: string };
            return {
              id: transaction.id,
              status: transaction.status,
              platformTransactionId: transaction.platform_transaction_id,
              createdAt: transaction.created_at,
              isTemporary: transaction.platform_transaction_id?.startsWith('temp_'),
              isReceiptBased: transaction.platform_transaction_id?.startsWith('ios_receipt_')
            };
          })
        });
      }

      // Check current product states for transaction data
      const windowWithCordova = window as unknown as { CdvPurchase?: { store?: { products?: Record<string, unknown>[] } } };
      const products = windowWithCordova.CdvPurchase?.store?.products || [];

      for (const product of products) {
        const productData = product as {
          id?: string;
          state?: string;
          owned?: boolean;
          transaction?: { id?: string; receipt?: string };
        };

        if (productData.owned) {
          this.log('IOS_IAP', 'info', `Owned product analysis: ${productData.id}`, {
            productId: productData.id,
            state: productData.state,
            owned: productData.owned,
            hasTransaction: !!productData.transaction,
            transactionId: productData.transaction?.id,
            hasReceipt: !!productData.transaction?.receipt,
            transactionComplete: !!(productData.transaction?.id && productData.transaction?.receipt)
          });
        }
      }

      // Check for callback flow issues
      this.analyzeCallbackFlow();

    } catch (error) {
      this.log('IOS_IAP', 'error', 'Failed to analyze IAP flow', { error });
    }
  }

  /**
   * Analyze callback flow for potential issues
   */
  private analyzeCallbackFlow(): void {
    try {
      // Look for patterns in the logged events that indicate callback flow issues
      const iapEvents = this.results.filter(r => r.category === 'IOS_IAP');

      const hasApprovedEvents = iapEvents.some(e => e.data?.eventType === 'purchase_approved');
      const hasVerificationTrigger = iapEvents.some(e => e.data?.eventType === 'verification_triggered');
      const hasVerifiedEvents = iapEvents.some(e => e.data?.eventType === 'purchase_verified');
      const hasSubscriptionMapping = iapEvents.some(e => e.data?.eventType === 'subscription_mapping_created');

      this.log('IOS_IAP', 'info', 'Callback flow analysis', {
        hasApprovedEvents,
        hasVerificationTrigger,
        hasVerifiedEvents,
        hasSubscriptionMapping,
        flowComplete: hasApprovedEvents && hasVerificationTrigger && hasVerifiedEvents && hasSubscriptionMapping,
        potentialIssues: {
          approvedButNoVerificationTrigger: hasApprovedEvents && !hasVerificationTrigger,
          verificationTriggeredButNotVerified: hasVerificationTrigger && !hasVerifiedEvents,
          verifiedButNoSubscriptionMapping: hasVerifiedEvents && !hasSubscriptionMapping
        }
      });
    } catch (error) {
      this.log('IOS_IAP', 'error', 'Failed to analyze callback flow', { error });
    }
  }

  /**
   * Get diagnostic results
   */
  getResults(): DiagnosticResult[] {
    return this.results;
  }

  /**
   * Get results by category
   */
  getResultsByCategory(category: string): DiagnosticResult[] {
    return this.results.filter(r => r.category === category);
  }

  /**
   * Get results by status
   */
  getResultsByStatus(status: DiagnosticResult['status']): DiagnosticResult[] {
    return this.results.filter(r => r.status === status);
  }

  /**
   * Generate summary report
   */
  generateSummary(): string {
    const categories = [...new Set(this.results.map(r => r.category))];
    const summary = categories.map(category => {
      const categoryResults = this.getResultsByCategory(category);
      const errors = categoryResults.filter(r => r.status === 'error').length;
      const warnings = categoryResults.filter(r => r.status === 'warning').length;
      const successes = categoryResults.filter(r => r.status === 'success').length;
      
      return `${category}: ${successes} ✅ | ${warnings} ⚠️ | ${errors} ❌`;
    }).join('\n');

    return `Subscription Diagnostics Summary:\n${summary}`;
  }
}

// Export singleton instance
export const subscriptionDiagnostics = new SubscriptionDiagnostics();
